import type { Source, Turn } from './api';

// ---------------------------------------------------------------------------
// Playing an episode that has never been rendered.
//
// A creator should not have to buy a render to find out whether their timing
// works. Everything the finished video is made of already exists before the
// render: the footage is in the library, the banners are rows, the bed is an
// asset, and the drawn scenes can be drawn one frame at a time by the same
// code the renderer uses. What was missing was a clock.
//
// This is the clock. It is pure on purpose — a play loop that owns its own
// arithmetic is a play loop nobody can test, and timing is the one thing this
// feature exists to get right.
//
// What it is NOT: a render. The transitions are the same faked crossfade the
// scrub already draws, the speech is not spoken, and a drawn scene is a still.
// Say so on the screen rather than letting a creator believe they have watched
// the finished thing.
// ---------------------------------------------------------------------------

/**
 * The biggest step the clock will take in one frame.
 *
 * A backgrounded tab stops calling requestAnimationFrame and hands back a
 * delta of however long it slept when it wakes. Without this, coming back to
 * the tab jumps the playhead to the end of the programme and stops — which
 * reads as "the preview crashed".
 */
export const MAX_STEP = 0.25;

export interface Tick {
  at: number;
  /** The programme ran out. The caller stops the clock and parks the head. */
  done: boolean;
}

/** Where the playhead goes after `dt` seconds of real time. */
export function tick(at: number, dt: number, total: number): Tick {
  const step = Math.min(Math.max(0, dt), MAX_STEP);
  const next = at + step;
  if (!(total > 0)) return { at: 0, done: true };
  if (next >= total) return { at: total, done: true };
  return { at: next, done: false };
}

/** How far into its own scene the playhead is. */
export function sceneElapsed(starts: number[], line: number, at: number): number {
  return Math.max(0, at - (starts[line] ?? 0));
}

/**
 * Where the footage should be, in its own timeline.
 *
 * A scene cuts to a piece of a clip: `sourceIn` is where that piece starts and
 * `sourceOut` where it ends. A scene that is held longer than its clip runs
 * freezes on the last frame rather than running into the next shot's footage,
 * which is what the renderer does and what a creator expects to see.
 */
export function footageTime(turn: Turn, elapsed: number): number {
  const from = Math.max(0, turn.sourceIn ?? 0);
  const to = typeof turn.sourceOut === 'number' && turn.sourceOut > from ? turn.sourceOut : null;
  const at = from + Math.max(0, elapsed) * (turn.speed && turn.speed > 0 ? turn.speed : 1);
  return to === null ? at : Math.min(at, to);
}

/**
 * Whether a scene's source is footage the stage can run, or a still.
 *
 * A source made from a picture carries no duration — the same signal the
 * timeline uses to colour its block. A still is already drawn by the frame
 * the preview fetched, caption and all; handing its JPEG to a <video>
 * element paints a black box over that picture for the whole scene, which
 * is exactly what "the images are not showing" looked like.
 */
export function isFootage(src: Pick<Source, 'duration_ms'> | null | undefined): boolean {
  return Boolean(src && typeof src.duration_ms === 'number' && src.duration_ms > 0);
}

/** The source a scene would run on the stage, or null when it is a still or has none. */
export function footageOf(turn: Pick<Turn, 'sourceId'> | null | undefined, sources: Pick<Source, 'id' | 'duration_ms'>[]): string | null {
  if (!turn?.sourceId) return null;
  return isFootage(sources.find((s) => s.id === turn.sourceId)) ? turn.sourceId : null;
}

/** Whether the clip's own sound is heard at all, from the scene's audio mode. */
export function footageMuted(turn: Turn): boolean {
  // 'over' — our voice carries the line and the clip is silent under it. It is
  // the default, and it is what every scene the Scene Builder writes uses.
  return (turn.audio ?? 'over') === 'over';
}

/**
 * How far the video may drift before it is corrected.
 *
 * Setting currentTime every frame makes the picture stutter, because each set
 * re-seeks the decoder. Left alone entirely, a clip that started late stays
 * late. So: let it run, and only pull it back when it is visibly wrong.
 */
export const DRIFT_S = 0.35;

export function needsSeek(actual: number, wanted: number): boolean {
  return Math.abs(actual - wanted) > DRIFT_S;
}

/**
 * How close to the end of a clip counts as the end of it.
 *
 * A video element that reaches its duration goes to `ended`, and calling
 * play() on an ended element starts it AGAIN FROM ZERO. Every scene here
 * outlives its clip — the beat after the line is silence the renderer holds —
 * so without this the last fraction of every scene showed the shot restarting.
 * One frame of the next take, ten times in eighty seconds.
 */
export const TAIL_S = 0.06;

export interface Held {
  /** Where to put the video's own clock. */
  at: number;
  /** The clip has run out: freeze on its last frame, the way the render does. */
  held: boolean;
}

/**
 * The video's clock and whether it should still be running.
 *
 * `duration` is the element's own, which is the authority — a source row's
 * duration_ms is what the ingest measured and can be a frame out.
 */
export function holdAt(wanted: number, duration: number | null): Held {
  if (duration === null || !Number.isFinite(duration) || duration <= 0) {
    return { at: wanted, held: false };
  }
  const last = Math.max(0, duration - TAIL_S);
  return wanted >= last ? { at: last, held: true } : { at: wanted, held: false };
}

/** What the preview cannot show, said plainly, in the order it matters. */
export function caveats(turn: Turn | null, hasFootage: boolean): string[] {
  const out: string[] = [];
  if (turn?.text?.trim()) out.push('the line is heard in its speaker\'s brand voice; a speaker with no voice is silent until the render');
  if (!hasFootage) out.push('a drawn scene is one frame');
  return out;
}
